/**
 * WhatsApp-style list scroll: stay pinned to the bottom for new items,
 * unless the user has scrolled up to read history.
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const NEAR_BOTTOM_PX = 120;

export function useStickToBottomScroll(
  deps: unknown[],
  opts?: { onNearTop?: () => void; nearTopPx?: number },
) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const stickRef = useRef(true);
  const restoreHeightRef = useRef<number | null>(null);
  /** Ignore scroll events caused by our own scrollToBottom / restore. */
  const programmaticRef = useRef(false);
  const onNearTopRef = useRef(opts?.onNearTop);
  onNearTopRef.current = opts?.onNearTop;
  const nearTopPx = opts?.nearTopPx ?? 48;

  const isNearBottom = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return true;
    // No overflow yet — treat as pinned so the first new messages stick.
    if (el.scrollHeight <= el.clientHeight + 8) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scrollerRef.current;
    if (!el) return;
    programmaticRef.current = true;
    stickRef.current = true;
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight, behavior });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollerRef.current) {
          scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
        programmaticRef.current = false;
        stickRef.current = true;
      });
    });
  }, []);

  const onScroll = useCallback(() => {
    if (programmaticRef.current) return;
    const el = scrollerRef.current;
    stickRef.current = isNearBottom();
    if (el && el.scrollTop <= nearTopPx && el.scrollHeight > el.clientHeight + 8) {
      onNearTopRef.current?.();
    }
  }, [isNearBottom, nearTopPx]);

  /** Force stick (e.g. channel switch / own send / panel open). */
  const pinToBottom = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      stickRef.current = true;
      scrollToBottom(behavior);
    },
    [scrollToBottom],
  );

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    const prevH = restoreHeightRef.current;
    if (prevH != null && el) {
      restoreHeightRef.current = null;
      programmaticRef.current = true;
      el.scrollTop += el.scrollHeight - prevH;
      requestAnimationFrame(() => {
        programmaticRef.current = false;
      });
      return;
    }
    if (!stickRef.current) return;
    scrollToBottom("auto");
    // Bubbles / fonts can grow height after the first paint.
    requestAnimationFrame(() => {
      if (stickRef.current) scrollToBottom("auto");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onResize = () => {
      if (stickRef.current) scrollToBottom("auto");
    };
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(onResize)
        : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [scrollToBottom]);

  return {
    scrollerRef,
    bottomRef,
    onScroll,
    pinToBottom,
    stickRef,
    isNearBottom,
    restoreHeightRef,
  };
}

export function sortByCreatedAtAsc<T extends { created_at?: string | null }>(
  rows: T[],
): T[] {
  return [...rows].sort((a, b) => {
    const aAt = Date.parse(String(a.created_at || "")) || 0;
    const bAt = Date.parse(String(b.created_at || "")) || 0;
    if (aAt !== bAt) return aAt - bAt;
    return 0;
  });
}
